import { DownOutlined } from "@ant-design/icons";
import {
	getFindingPerson,
	getFindingStatus,
} from "../../../utils/reporting/findingFilters";
import { capitalizeFirstLetter } from "../../../utils/string_convert_helper";
import ReportItemEditButton from "../../Button/ReportItemEditButton";
import {
	ReportItemAttachments,
	ReportItemDeadline,
	ReportItemOutcome,
	ReportItemPerson,
	ReportItemRecommendedAction,
	ReportItemStages,
	ReportItemStatusTag,
	ReportItemTags,
	ReportItemViewProps,
	getReportItemDocuments,
} from "./ReportItemParts";

type ReportItemRowListProps = ReportItemViewProps & {
	// The report positions of the items that are open.
	expanded: Set<number>;
	onToggle: (index: number) => void;
};

// The List view of a report's items: one row per item, which opens to show
// its stages, the recommended action (audits only), the outcome and the
// attachments.
function ReportItemRowList(props: Readonly<ReportItemRowListProps>) {
	const {
		kind,
		entries,
		showStatus,
		showType,
		stageReport,
		canEditItem,
		onEditItem,
		onOpenDocument,
		expanded,
		onToggle,
	} = props;
	// "Finding" or "Action", for the column and each row's number.
	const itemName = capitalizeFirstLetter(kind.noun);
	const recommendedField = kind.recommendedActionField;

	return (
		<div className="report-items__list">
			<div className="report-items__list-head" aria-hidden="true">
				<span className="report-items__col-number">No.</span>
				<span className="report-items__col-item">{itemName}</span>
				<span className="report-items__col-person">Person</span>
				<span className="report-items__col-deadline">Deadline</span>
				{showStatus && <span className="report-items__col-status">Status</span>}
				<span className="report-items__col-chevron" />
			</div>
			<ul className="report-items__rows">
				{entries.map((entry) => {
					const { item, index } = entry;
					const isOpen = expanded.has(index);
					const panelId = `report-item-panel-${index}`;
					const documents = getReportItemDocuments(item);
					const description = item?.[kind.descriptionField] || "—"; // NOSONAR: empty text shows a dash

					return (
						<li key={index} className="report-items__row">
							<div className="report-items__row-main">
								<button
									type="button"
									className="report-items__row-toggle"
									aria-expanded={isOpen}
									aria-controls={isOpen ? panelId : undefined}
									onClick={() => onToggle(index)}
								>
									<span className="report-items__col-number">
										<span className="report-items__number-badge">
											<span className="sr-only">{`${itemName} `}</span>
											{index + 1}
										</span>
									</span>
									<span className="report-items__col-item">
										<span
											className={
												isOpen
													? "report-items__row-description"
													: "report-items__row-description report-items__row-description--clamped"
											}
										>
											{description}
										</span>
										<ReportItemTags
											item={item}
											kind={kind}
											attachmentCount={documents.length}
											showType={showType}
										/>
									</span>
									<span className="report-items__col-person">
										<ReportItemPerson item={item} />
										<span className="report-items__person-caption">
											{getFindingPerson(item).label}
										</span>
									</span>
									<span className="report-items__col-deadline">
										<ReportItemDeadline entry={entry} />
									</span>
									{showStatus && (
										<span className="report-items__col-status">
											<ReportItemStatusTag status={getFindingStatus(item)} />
										</span>
									)}
									<span className="report-items__col-chevron">
										<DownOutlined
											className={
												isOpen
													? "report-items__chevron report-items__chevron--open"
													: "report-items__chevron"
											}
										/>
									</span>
								</button>
								<span className="report-items__row-edit">
									{onEditItem && canEditItem?.(item) && (
										<ReportItemEditButton
											itemNumber={kind.getTitle(index)}
											onClick={() => onEditItem(index)}
										/>
									)}
								</span>
							</div>
							{isOpen && (
								<div id={panelId} className="report-items__row-panel">
									<div className="report-items__row-stages">
										<ReportItemStages
											item={item}
											report={stageReport}
											kind={kind}
										/>
									</div>
									{recommendedField && (
										<ReportItemRecommendedAction
											text={item?.[recommendedField]}
										/>
									)}
									<ReportItemOutcome
										entry={entry}
										kind={kind}
										showStatus={showStatus}
										className="report-items__row-outcome"
									/>
									<div className="report-items__field">
										<span className="report-items__label">Attachments</span>
										<ReportItemAttachments
											documents={documents}
											onOpenDocument={onOpenDocument}
										/>
									</div>
								</div>
							)}
						</li>
					);
				})}
			</ul>
		</div>
	);
}

export default ReportItemRowList;
